import React from 'react';
import styled from 'styled-components';
import { FeedOptions } from '../types/rss';
import { FiGrid, FiList, FiCreditCard, FiSun, FiMoon, FiMonitor, FiSettings } from 'react-icons/fi';

interface SettingsPanelProps {
  options: FeedOptions;
  onUpdateOptions: (newOptions: Partial<FeedOptions>) => void;
  isOpen: boolean;
  onClose: () => void;
}

const Panel = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen'
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: ${props => props.isOpen ? '0' : '-320px'};
  width: 320px;
  height: 100%;
  background-color: ${props => {
    const isDark = document.body.classList.contains('dark-theme');
    return isDark ? '#2d2d2d' : 'white';
  }};
  color: ${props => {
    const isDark = document.body.classList.contains('dark-theme');
    return isDark ? '#ffffff' : '#000000';
  }};
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  padding: 24px;
  transition: right 0.3s ease;
  z-index: 1000;
  overflow-y: auto;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PanelTitle = styled.h2`
  font-size: 20px;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  color: inherit;
`;

const SettingSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  margin-bottom: 12px;
  font-weight: 500;
`;

const OptionGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const OptionButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'active'
})<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  background-color: ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'inherit'};
  border: 1px solid ${props => {
    if (props.active) return 'var(--primary-color)';
    const isDark = document.body.classList.contains('dark-theme');
    return isDark ? '#555' : '#ddd';
  }};
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    background-color: ${props => {
      if (props.active) return 'var(--primary-color)';
      const isDark = document.body.classList.contains('dark-theme');
      return isDark ? '#404040' : '#f0f0f0';
    }};
  }
`;

const SwitchContainer = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const SwitchLabel = styled.span`
  margin-left: 8px;
`;

const Switch = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'checked'
})<{ checked: boolean }>`
  position: relative;
  width: 40px;
  height: 20px;
  background-color: ${props => {
    if (props.checked) return 'var(--primary-color)';
    const isDark = document.body.classList.contains('dark-theme');
    return isDark ? '#555' : '#ccc';
  }};
  border-radius: 20px;
  transition: var(--transition);
  
  &:before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: white;
    top: 2px;
    left: ${props => props.checked ? '22px' : '2px'};
    transition: var(--transition);
  }
`;

const RangeContainer = styled.div`
  margin-top: 8px;
`;

const RangeLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
`;

const RangeInput = styled.input`
  width: 100%;
  -webkit-appearance: none;
  height: 4px;
  background: ${props => {
    const isDark = document.body.classList.contains('dark-theme');
    return isDark ? '#555' : '#ddd';
  }};
  border-radius: 2px;
  outline: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--primary-color);
    cursor: pointer;
  }
`;

const InputContainer = styled.div`
  margin-top: 8px;
`;

const InputLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${props => {
    const isDark = document.body.classList.contains('dark-theme');
    return isDark ? '#555' : '#ddd';
  }};
  background-color: ${props => {
    const isDark = document.body.classList.contains('dark-theme');
    return isDark ? '#404040' : 'white';
  }};
  color: ${props => {
    const isDark = document.body.classList.contains('dark-theme');
    return isDark ? '#ffffff' : '#000000';
  }};
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  transition: var(--transition);
  
  &:focus {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const ColorInput = styled.input`
  width: 100%;
  height: 40px;
  border: 1px solid ${props => {
    const isDark = document.body.classList.contains('dark-theme');
    return isDark ? '#555' : '#ddd';
  }};
  border-radius: 4px;
  cursor: pointer;
  outline: none;
  transition: var(--transition);
  
  &:focus {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const ColorPreview = styled.div<{ color: string }>`
  width: 30px;
  height: 30px;
  border-radius: 4px;
  background-color: ${props => props.color};
  border: 1px solid ${props => {
    const isDark = document.body.classList.contains('dark-theme');
    return isDark ? '#555' : '#ddd';
  }};
  margin-right: 8px;
`;

const ColorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const Overlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen'
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

export const SettingsPanel: React.FC<SettingsPanelProps> = ({ options, onUpdateOptions, isOpen, onClose }) => {
  return (
    <>
      <Overlay isOpen={isOpen} onClick={onClose} />
      <Panel isOpen={isOpen}>
        <PanelHeader>
          <PanelTitle>Configurações</PanelTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </PanelHeader>
        
        <SettingSection>
          <SectionTitle>Layout</SectionTitle>
          <OptionGroup>
            <OptionButton 
              active={options.layout === 'grid'}
              onClick={() => onUpdateOptions({ layout: 'grid' })}
            >
              <span><FiGrid /></span> Grade
            </OptionButton>
            <OptionButton 
              active={options.layout === 'list'}
              onClick={() => onUpdateOptions({ layout: 'list' })}
            >
              <span><FiList /></span> Lista
            </OptionButton>
            <OptionButton 
              active={options.layout === 'card'}
              onClick={() => onUpdateOptions({ layout: 'card' })}
            >
              <span><FiCreditCard /></span> Cards
            </OptionButton>
          </OptionGroup>
        </SettingSection>
        
        <SettingSection>
          <SectionTitle>Tema</SectionTitle>
          <OptionGroup>
            <OptionButton 
              active={options.theme === 'light'}
              onClick={() => onUpdateOptions({ theme: 'light' })}
            >
              <span><FiSun /></span> Claro
            </OptionButton>
            <OptionButton 
              active={options.theme === 'dark'}
              onClick={() => onUpdateOptions({ theme: 'dark' })}
            >
              <span><FiMoon /></span> Escuro
            </OptionButton>
            <OptionButton 
              active={options.theme === 'auto'}
              onClick={() => onUpdateOptions({ theme: 'auto' })}
            >
              <span><FiMonitor /></span> Auto
            </OptionButton>
          </OptionGroup>
        </SettingSection>
        
        <SettingSection>
          <SectionTitle>Exibição</SectionTitle>
          <SwitchContainer>
            <Switch 
              checked={options.showImages} 
              onClick={() => onUpdateOptions({ showImages: !options.showImages })}
            />
            <SwitchLabel>Mostrar imagens</SwitchLabel>
          </SwitchContainer>
        </SettingSection>
        
        <SettingSection>
          <SectionTitle>Ordenação</SectionTitle>
          <OptionGroup>
            <OptionButton 
              active={options.sortBy === 'newest'}
              onClick={() => onUpdateOptions({ sortBy: 'newest' })}
            >
              Mais recentes
            </OptionButton>
            <OptionButton 
              active={options.sortBy === 'oldest'}
              onClick={() => onUpdateOptions({ sortBy: 'oldest' })}
            >
              Mais antigas
            </OptionButton>
          </OptionGroup>
        </SettingSection>
        
        <SettingSection>
          <SectionTitle>Fonte RSS</SectionTitle>
          <InputContainer>
            <InputLabel>URL do Feed RSS</InputLabel>
            <TextInput 
              type="url"
              value={options.rssUrl}
              onChange={(e) => onUpdateOptions({ rssUrl: e.target.value })}
              placeholder="https://feeds.folha.uol.com.br/emcimadahora/rss091.xml"
            />
          </InputContainer>
        </SettingSection>
        
        <SettingSection>
          <SectionTitle><span><FiSettings /></span> Personalização</SectionTitle>
          <SwitchContainer>
            <Switch 
              checked={options.customTheme} 
              onClick={() => onUpdateOptions({ customTheme: !options.customTheme })}
            />
            <SwitchLabel>Usar tema personalizado</SwitchLabel>
          </SwitchContainer>
          
          {options.customTheme && (
            <>
              <InputContainer>
                <InputLabel>Tamanho da fonte</InputLabel>
                <RangeContainer>
                  <RangeLabel>
                    <span>12px</span>
                    <span>{options.fontSize}px</span>
                    <span>24px</span>
                  </RangeLabel>
                  <RangeInput 
                    type="range" 
                    min="12" 
                    max="24" 
                    value={options.fontSize}
                    onChange={(e) => onUpdateOptions({ fontSize: parseInt(e.target.value) })}
                  />
                </RangeContainer>
              </InputContainer>
              
              <InputContainer>
                <InputLabel>Cor da fonte</InputLabel>
                <ColorContainer>
                  <ColorPreview color={options.fontColor} />
                  <ColorInput 
                    type="color"
                    value={options.fontColor}
                    onChange={(e) => onUpdateOptions({ fontColor: e.target.value })}
                  />
                </ColorContainer>
              </InputContainer>
              
              <InputContainer>
                <InputLabel>Cor de fundo</InputLabel>
                <ColorContainer>
                  <ColorPreview color={options.backgroundColor} />
                  <ColorInput 
                    type="color"
                    value={options.backgroundColor}
                    onChange={(e) => onUpdateOptions({ backgroundColor: e.target.value })}
                  />
                </ColorContainer>
              </InputContainer>
            </>
          )}
        </SettingSection>

        <SettingSection>
          <SectionTitle>Intervalo de atualização</SectionTitle>
          <RangeContainer>
            <RangeLabel>
              <span>1 min</span>
              <span>{options.refreshInterval} min</span>
              <span>30 min</span>
            </RangeLabel>
            <RangeInput 
              type="range" 
              min="1" 
              max="30" 
              value={options.refreshInterval}
              onChange={(e) => onUpdateOptions({ refreshInterval: parseInt(e.target.value) })}
            />
          </RangeContainer>
        </SettingSection>

        <SettingSection>
          <SectionTitle>Velocidade do Ticker</SectionTitle>
          <RangeContainer>
            <RangeLabel>
              <span>Lento (0.5x)</span>
              <span>{options.tickerSpeed}x</span>
              <span>Rápido (3x)</span>
            </RangeLabel>
            <RangeInput 
              type="range" 
              min="0.5" 
              max="3" 
              step="0.1"
              value={options.tickerSpeed}
              onChange={(e) => onUpdateOptions({ tickerSpeed: parseFloat(e.target.value) })}
            />
          </RangeContainer>
        </SettingSection>
      </Panel>
    </>
  );
};